"use client";

import React from "react";
import { ArrowRight, Layers, FileText, Palette, MousePointerClick, ShoppingBag, ShieldCheck, Lock, Globe, BadgeCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function ScalingTravelGrowthHero() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const items = [{
    icon: <ShieldCheck className="w-8 h-8" />,
    title: "GDPR Compliant",
    text: "Built with privacy-first principles and full GDPR compliance for secure handling of European user data."
  }, {
    icon: <Globe className="w-8 h-8" />,
    title: "Hosted in Switzerland",
    text: "Your infrastructure is securely hosted in Switzerland with strong data protection standards and reliability."
  }, {
    icon: <BadgeCheck className="w-8 h-8" />,
    title: "ISO 27001 Certified",
    text: "Enterprise-grade information security processes aligned with internationally recognized ISO 27001 standards."
  }, {
    icon: <Lock className="w-8 h-8" />,
    title: "EU AI Act Ready",
    text: "Designed following emerging EU AI Act guidelines for transparency, accountability, and responsible AI usage."
  }];
  return <section id="the-future-of-web" className="relative w-full overflow-hidden bg-background py-20 lg:py-28 border-t-2 border-[#F78E1Dcc]">
      {/* Background */}
      <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
        <svg className="absolute h-full w-full object-cover" preserveAspectRatio="none" viewBox="0 0 1440 800" fill="none" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient id="wave-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="var(--muted)" />
              <stop offset="100%" stopColor="#ffffff" />
            </linearGradient>
          </defs>
          
          <path opacity="0.9" d="M1440 150C1000 350 400 -50 0 150V800H1440V150Z" fill="url(#wave-gradient)">
            <animate attributeName="d" dur="7s" repeatCount="indefinite" values="
        M1440 150C1000 350 400 -50 0 150V800H1440V150Z;
        M1440 180C1050 50 350 250 0 120V800H1440V180Z;
        M1440 150C1000 350 400 -50 0 150V800H1440V150Z
      " />
          </path>
        </svg>
      </div>

      <div className="relative z-10 mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        <div ref={ref} className={`flex flex-col items-center text-center transition-all duration-1000 ease-out ${isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0"}`}>

          {/* Heading */}
          <h1 className="max-w-4xl text-5xl font-bold tracking-tight text-foreground sm:text-6xl lg:text-7xl leading-[1.05]">
            Build Stunning Websites
            <span className="block text-primary italic">
              From a Single Prompt
            </span>
          </h1>

          {/* Description */}
          <p className="mt-6 max-w-2xl text-lg leading-8 text-foreground/70">
            Describe your idea and let our 
            {" "}<span className="text-[#F78E1Dcc] text-lg">
               Agentic 
            </span>{" "}  AI generate a complete modern website in
            seconds.
          </p>

          {/* Prompt Box */}
          <div className="mt-10 w-full max-w-3xl rounded-3xl border border-border bg-background/80 p-4 shadow-2xl backdrop-blur">
            <Textarea placeholder="Create a modern SaaS landing page for an AI research platform with dark mode, pricing section, testimonials, and smooth animations..." className="min-h-[160px] resize-none rounded-2xl border-0 bg-transparent p-4 text-base shadow-none focus-visible:ring-0 placeholder:text-gray-400" />

            <div className="mt-4 ml-4 flex items-center justify-between gap-4">
              <p className="text-sm text-muted-foreground">
                Powered by Werkys AI.
              </p>

              <Button size="lg" className="rounded-2xl px-8 py-6 text-base font-semibold shadow-lg group">
                <a href='https://account.werkys.com/sign-up'>Create Website</a>
                <ArrowRight className="ml-2 size-5 transition-transform group-hover:translate-x-1" />
              </Button>
            </div>
          </div>
          <div className='flex flex-row mt-6 gap-6 flex-wrap'> 
            {/* Layouts Badge */} 
            <div className="mb-6 flex items-center gap-2 rounded-full border border-border bg-background/70 px-4 py-2 text-sm text-muted-foreground backdrop-blur"> 
              <Layers className="h-4 w-4" />
              <span>Layouts</span> 
            </div> 
          
            {/* Content Badge */} 
            <div className="mb-6 flex items-center gap-2 rounded-full border border-border bg-background/70 px-4 py-2 text-sm text-muted-foreground backdrop-blur"> 
              <FileText className="h-4 w-4" />
              <span>Content</span> 
            </div> 
          
            {/* Styling Badge */} 
            <div className="mb-6 flex items-center gap-2 rounded-full border border-border bg-background/70 px-4 py-2 text-sm text-muted-foreground backdrop-blur"> 
              <Palette className="h-4 w-4" />
              <span>Styling</span> 
            </div> 
          
            {/* Interactions Badge */} 
            <div className="mb-6 flex items-center gap-2 rounded-full border border-border bg-background/70 px-4 py-2 text-sm text-muted-foreground backdrop-blur"> 
              <MousePointerClick className="h-4 w-4" />
              <span>Interactions</span> 
            </div> 
          
            {/* e-Commerce Badge */} 
            <div className="mb-6 flex items-center gap-2 rounded-full border border-border bg-background/70 px-4 py-2 text-sm text-muted-foreground backdrop-blur"> 
              <ShoppingBag className="h-4 w-4" />
              <span>e-Commerce</span> 
            </div> 
          </div>
        </div>
      </div>
      <section className="w-full py-16 px-6 text-white overflow-hidden">
      <div className="max-w-7xl mx-auto ">
        {/* Header */}
        <div className="text-center mb-14 animate-fade-in-up">
          <span className="inline-block px-4 py-1 rounded-full bg-white/10 border border-white/20 text-sm tracking-wide mb-4">
            Security & Compliance
          </span>

          <h2 className="text-4xl md:text-5xl font-bold leading-tight text-black">
            Enterprise-grade
            <span className="bg-gradient-to-r from-orange-300 to-orange-500 bg-clip-text text-transparent">
              {" "}
              compliance & security
            </span>
          </h2>

          <p className="mt-6 max-w-2xl mx-auto text-lg leading-8 text-foreground/70">
            Built for organizations that require strict privacy, secure
            infrastructure, and responsible AI governance.
          </p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
          {items.map((item, index) => <div key={index} className="group relative p-6 rounded-3xl border border-orange-200/40 bg-orange-50/40 backdrop-blur-xl hover:bg-orange-100/50 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl hover:shadow-orange-400/20">
              {/* Glow */}
              <div className="absolute inset-0 rounded-3xl opacity-0 group-hover:opacity-100 transition duration-500 bg-gradient-to-br from-orange-200/40 to-orange-500/20 blur-xl"></div>
        
              {/* Content */}
              <div className="relative z-10 text-black">
                <div className="mb-5 inline-flex p-3 rounded-2xl bg-gradient-to-br from-orange-200 to-orange-400 text-orange-700 group-hover:scale-110 transition-transform duration-300">
                  {item.icon}
                </div>
        
                <h3 className="text-xl font-semibold mb-3 text-black">
                  {item.title}
                </h3>
        
                <p className="text-black/70 leading-relaxed text-sm">
                  {item.text}
                </p>
              </div>
        
              {/* Animated border */}
              <div className="absolute inset-0 rounded-3xl border border-transparent group-hover:border-orange-300/60 transition-all duration-500"></div>
            </div>)}
        </div>
      </div>

      {/* Animations */}
      <style jsx>{`
        .animate-fade-in-up {
          animation: fadeUp 0.8s ease-out forwards;
        }

        @keyframes fadeUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </section>
    </section>;
}